/**
 * List toolbar shell: search + optional filter slot + optional actions.
 * Page owns filter semantics; this only provides layout chrome.
 *
 * Desktop: actions stay left, filters sit right. If the row is short, filters
 * wrap onto the next line instead of sliding under the actions.
 * Small widths: Filters toggle; Email/Export/Import stay visible.
 */
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/outline';
import ListSearchInput from './ListSearchInput';
import {
  toolbarFiltersPanel,
  toolbarStandaloneActive,
  toolbarStandaloneBase,
  toolbarStandaloneInactive,
} from './listToolbarTokens';

export const TOOLBAR_COMPACT_MAX = 720;

export type ToolbarControlLayout = 'compact' | 'wide';

export function resolveToolbarControlLayout(width: number): ToolbarControlLayout {
  return width < TOOLBAR_COMPACT_MAX ? 'compact' : 'wide';
}

export type ListSearchFilterBarProps = {
  search: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    'aria-label': string;
    disabled?: boolean;
  };
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  hint?: React.ReactNode;
  stacked?: boolean;
  filtersActive?: boolean;
  filtersActiveCount?: number;
};

const ListSearchFilterBar: React.FC<ListSearchFilterBarProps> = ({
  search,
  filters,
  actions,
  className = '',
  hint,
  stacked = false,
  filtersActive = false,
  filtersActiveCount = 0,
}) => {
  const controlsRef = useRef<HTMLDivElement>(null);
  const compactRootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const panelId = useId();

  const layout =
    width == null || width === 0 ? 'wide' : resolveToolbarControlLayout(width);
  const hasFilters = Boolean(filters);
  const hasActions = Boolean(actions);
  const showCompactFilters = layout === 'compact' && hasFilters;

  useLayoutEffect(() => {
    const el = controlsRef.current;
    if (!el) return;

    const update = () => {
      const next = el.getBoundingClientRect().width;
      setWidth(next);
    };
    update();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (typeof next === 'number') setWidth(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasFilters, hasActions]);

  useEffect(() => {
    if (!showCompactFilters) setFiltersOpen(false);
  }, [showCompactFilters]);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (compactRootRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[role="menu"]')) return;
      setFiltersOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFiltersOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [filtersOpen]);

  const actionsGroup = actions ? (
    <div className="flex shrink-0 items-center gap-2">{actions}</div>
  ) : null;

  const filtersGroup = filters ? (
    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
      {filters}
    </div>
  ) : null;

  let controls: React.ReactNode = null;
  if (hasFilters || hasActions) {
    if (showCompactFilters) {
      controls = (
        <div
          ref={compactRootRef}
          className="flex w-full min-w-0 flex-wrap items-center gap-2"
        >
          {actionsGroup}
          <div className="relative shrink-0">
            <button
              type="button"
              aria-label={
                filtersActiveCount > 0
                  ? `Filters (${filtersActiveCount} active)`
                  : 'Filters'
              }
              aria-expanded={filtersOpen}
              aria-controls={panelId}
              onClick={() => setFiltersOpen((open) => !open)}
              className={`${toolbarStandaloneBase} justify-between gap-1.5 ${
                filtersActive || filtersOpen
                  ? toolbarStandaloneActive
                  : toolbarStandaloneInactive
              }`}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <FunnelIcon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate font-semibold">Filters</span>
                {filtersActiveCount > 0 ? (
                  <span className="ml-0.5 min-w-[18px] rounded-full bg-white px-1.5 py-0.5 text-center text-xs font-bold text-primary-500">
                    {filtersActiveCount}
                  </span>
                ) : null}
              </span>
              <ChevronDownIcon
                className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${
                  filtersOpen ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            </button>
          </div>
          {filtersOpen ? (
            <div
              id={panelId}
              role="region"
              aria-label="All filters"
              className={`${toolbarFiltersPanel} basis-full flex flex-wrap justify-start gap-2`}
            >
              {filters}
            </div>
          ) : null}
        </div>
      );
    } else {
      controls = (
        <div
          className={`flex min-w-0 w-full flex-nowrap items-start gap-2 ${
            stacked ? '' : '2xl:w-auto'
          } ${
            hasFilters && hasActions
              ? 'justify-between'
              : hasFilters
                ? 'justify-end'
                : ''
          }`}
        >
          {actionsGroup}
          {filtersGroup}
        </div>
      );
    }
  }

  const searchField = (
    <ListSearchInput
      value={search.value}
      onChange={search.onChange}
      placeholder={search.placeholder}
      aria-label={search['aria-label']}
      disabled={search.disabled}
    />
  );

  return (
    <div className={`flex flex-col gap-1.5 min-w-0 ${className}`}>
      <div
        className={`flex min-w-0 flex-col items-stretch gap-2 ${
          stacked ? '' : '2xl:flex-row 2xl:items-center'
        }`}
      >
        {searchField}
        {controls ? (
          <div
            ref={controlsRef}
            className="w-full min-w-0"
            data-toolbar-layout={layout}
          >
            {controls}
          </div>
        ) : null}
      </div>
      {hint ? (
        <div className="text-xs text-primary-500/60">{hint}</div>
      ) : null}
    </div>
  );
};

export default ListSearchFilterBar;
